/** Staff-only "Resend order email" card — re-sends the office order email for any brand (optionally to the installer). */
import React, { useState } from 'react';
import { Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import API_URL from '@/config/api';
import { formatApiError } from '@/utils/apiError';

export async function resendOrderEmail(product, orderId, { toInstaller = false, items = [] } = {}) {
  const res = await fetch(`${API_URL}/api/staff/orders/${product}/${orderId}/resend-email`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ to_installer: toInstaller, items }) });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(formatApiError(d) || 'Failed to resend');
  return d;
}

export function ResendOrderEmailCard({ product, order, accent = '#1f4030', onSent, testIdPrefix = 'resend' }) {
  const [busy, setBusy] = useState(false);
  const [toInstaller, setToInstaller] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const d = await resendOrderEmail(product, order.id, { toInstaller });
      toast.success(`Order email re-sent to ${d.sent_to.join(', ')}`);
      onSent?.(d);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Card className="p-4 flex flex-wrap items-center gap-3" data-testid={`${testIdPrefix}-card`}>
      <Mail className="w-4 h-4" style={{ color: accent }} />
      <div className="flex-1 min-w-[200px] text-sm">
        <div className="font-semibold text-gray-900">Resend order email</div>
        <div className="text-xs text-gray-500">{order.email_resent_at ? `Last re-sent ${new Date(order.email_resent_at).toLocaleString('en-GB')} by ${order.email_resent_by || 'staff'}` : 'Sends the full order with its product lines to the Ox Resin office inbox again.'}</div>
      </div>
      <label className="inline-flex items-center gap-1.5 text-xs text-gray-700"><input type="checkbox" checked={toInstaller} onChange={(e) => setToInstaller(e.target.checked)} data-testid={`${testIdPrefix}-installer`} /> Also send to installer</label>
      <Button size="sm" onClick={send} disabled={busy} className="text-white" style={{ background: accent }} data-testid={`${testIdPrefix}-btn`}>{busy ? 'Sending…' : 'Resend order email'}</Button>
    </Card>
  );
}
